import classnames from 'classnames/bind'
import { useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { useDebouncedCallback } from 'use-debounce'
import { GlobalThemeColors } from '~/@types/colors'
import { Variant } from '~/@types/product'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { CUSTOMIZATION_TAGS } from '~/lib/constants'

import Ratio from '~/components/Abstracts/Ratio'
import Tag from '~/components/Abstracts/Tag'
import Checkbox from '~/components/Form/Checkbox'
import Input from '~/components/Form/Input'
import BlurredCta from '~/components/UI/BlurredCta'
import { VariantImageSelector } from '~/components/UI/ProductVariantSelector/VariantImageSelector'

import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useStyle } from '~/providers/StyleProvider'

import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'
import useLocale from '~/hooks/useLocale'

import { convertBlobToBase64 } from '~/utils/convert-blob-to-base64'

import { CTA, CUSTOMIZER, PRODUCT, PRODUCTS_TYPES } from '~/data/dictionary'
import { SerializedProduct } from '~/data/product-card-data'
import serializePrice from '~/data/serialize-price'

import css from './styles.module.scss'

const cx = classnames.bind(css)
export interface LabelCustomizationProps {
  className?: string
  click?(): void
  product: SerializedProduct
  active?: boolean
  onChange?: (any) => void
  disabled: boolean
}

// This is a placeholder for the image in the HTML string
// returnted by akairos API
const IMAGE_PLACEHOLDER = 'IMG-BASE64-CODE'

// 5MB
const MAX_BYTES_SIZE = 5 * 1024 * 1024

export default function LabelCustomization({
  className,
  click,
  product,
  active,
  onChange,
  disabled,
}: LabelCustomizationProps) {
  const tags = product?.tags
  const locale = useLocale()
  const t = useTranslate()
  const variants = product.variants
  const [lastActiveInput, setLastActive] = useState<'title' | 'subtitle'>(null)
  const inputRef = useRef(null)
  const [fileError, setFileError] = useState(false)
  const {
    currentLabelInputsContent,
    customizationImageState: [customizationImage, setCustomizationImage],
  } = useProductHeaderProvider()

  const { activeTabIndex } = useGetStateInformationsFromUserSelection()
  const [isTyping, setTyping] = useState(false)
  const [activeVariant, setActiveVariant] = useState(product?.defaultVariant)
  const [title, setTitle] = useState(currentLabelInputsContent?.title)
  const [subtitle, setSubtitle] = useState(currentLabelInputsContent?.subtitle)
  const [titleError] = useState(
    currentLabelInputsContent?.title?.length !== title?.length,
  )

  const shouldHidePreviewOnMobile = variants.length === 1

  const withSubtitle = isLabelWithSubtitle(product, activeVariant)

  const [subtitleError] = useState(
    currentLabelInputsContent?.subtitle?.length !== subtitle?.length,
  )

  const errorStyle = useStyle({
    textPreset: GlobalTextPreset.Label10Primary,
    color: GlobalThemeColors.ErrorCompareAtPrice,
  })

  useEffect(() => {
    if (!currentLabelInputsContent || !title || isTyping) return
    if (
      disabled ||
      // empty string will return false so we at least make sure we have strings here
      typeof currentLabelInputsContent?.title !== 'string' ||
      typeof currentLabelInputsContent?.subtitle !== 'string'
    )
      return
    setTitle(currentLabelInputsContent?.title)

    if (withSubtitle && subtitle !== '') {
      setSubtitle(currentLabelInputsContent?.subtitle)
    }
  }, [currentLabelInputsContent])

  useEffect(() => {
    if (!title) {
      setTitle(currentLabelInputsContent?.title)
    }
    if (!subtitle) {
      setSubtitle(currentLabelInputsContent?.subtitle)
    }
  }, [currentLabelInputsContent])

  const textareaTextStyle = useStyle({
    textPreset: GlobalTextPreset.Input16Primary,
  })

  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Cta12PrimaryReg,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const variant = product?.variants?.find(
    (item) => item?.id === activeVariant?.id,
  )

  const priceDifference = `${
    variant?.price?.amount > 0 ? '+' : ''
  }${serializePrice(
    locale,
    variant?.price?.currencyCode,
    variant?.price?.amount,
  )}`

  const onType = useDebouncedCallback(() => {
    setTyping(false)
    if (!active) return
    onChange?.({
      type: product.customizationType,
      variant,
      title,
      subtitle,
      product,
      price: variant?.price.amount,
      computeSubtitleFirst: lastActiveInput === 'title',
    })
  }, 500)

  useEffect(() => {
    onType()
  }, [activeVariant, active])

  useEffect(() => {
    if (!active) return

    if (disabled) {
      click?.()
      onChange?.(null)
    }
  }, [disabled])

  const handleClick = (e) => {
    e.preventDefault()

    click?.()
  }

  useEffect(() => {
    if (product.customizationType !== 'label_with_image') return
    if (customizationImage[activeTabIndex] === null || !active) {
      if (inputRef.current) {
        inputRef.current.value = ''
      }

      let nexcustomizationImageState = customizationImage
      nexcustomizationImageState[activeTabIndex] = null
      setCustomizationImage([...nexcustomizationImageState])
    }
  }, [JSON.stringify(customizationImage), active])

  const TitleInput = () => {
    return (
      <div
        className={cx(css.textareaContainer, {
          hide: !activeVariant?.isLabelWithTitle,
        })}>
        <Input
          onKeyDown={() => setLastActive('title')}
          value={title}
          onChange={(e) => {
            setTyping(true)
            setTitle(e.currentTarget.value)
            onType()
          }}
          placeholder={t(CUSTOMIZER.LABEL_TITLE)}
          className={cx(textareaTextStyle, css.titleInput)}
        />
        {titleError && (
          <div className={cx(css.error, errorStyle)}>
            {t(CUSTOMIZER.FIELD_TOO_LONG)}
          </div>
        )}
      </div>
    )
  }

  const customization = (type) => {
    switch (type) {
      case CUSTOMIZATION_TAGS.ENGRAVING:
        return (
          <>
            <div className={cx(css.variantsImages)}>
              {variants?.length > 1 &&
                variants?.map((item) => {
                  return (
                    <VariantImageSelector
                      key={item?.id}
                      ariaLabel={`${t(
                        PRODUCTS_TYPES.TYPE(product.productType),
                      )}: ${item?.selectedOptions?.[0]?.value}`}
                      className={cx(css.variantImageSelector)}
                      active={item?.id === activeVariant?.id}
                      onClick={() => setActiveVariant(item)}
                      item={item}
                    />
                  )
                })}
            </div>
            {TitleInput()}

            <div
              className={cx(css.textareaContainer, {
                hide: !activeVariant?.isEngravingWithSubtitle,
              })}>
              <Input
                textarea
                onKeyDown={() => setLastActive('subtitle')}
                value={subtitle}
                onChange={(e) => {
                  setTyping(true)
                  setSubtitle(e.currentTarget.value)
                  onType()
                }}
                placeholder={t(CUSTOMIZER.LABEL_SUBTITLE)}
                className={cx(textareaTextStyle, css.subtitleInput)}
              />
            </div>
          </>
        )
      case CUSTOMIZATION_TAGS.LABEL:
        return (
          <>
            <div className={cx(css.variantsImages)}>
              {variants?.length > 1 &&
                variants?.map((item) => {
                  return (
                    <VariantImageSelector
                      key={item?.id}
                      ariaLabel={`${t(
                        PRODUCTS_TYPES.TYPE(product.productType),
                      )}: ${item?.selectedOptions?.[0]?.value}`}
                      className={cx(css.variantImageSelector)}
                      active={item?.id === activeVariant?.id}
                      onClick={() => setActiveVariant(item)}
                      item={item}
                    />
                  )
                })}
            </div>
            {TitleInput()}
            <div
              className={cx(css.textareaContainer, {
                hide: !activeVariant?.isLabelWithSubtitle,
              })}>
              <Input
                textarea
                onKeyDown={() => setLastActive('subtitle')}
                value={subtitle}
                onChange={(e) => {
                  setTyping(true)
                  setSubtitle(e.currentTarget.value)
                  onType()
                }}
                placeholder={t(CUSTOMIZER.LABEL_SUBTITLE)}
                className={cx(textareaTextStyle, css.subtitleInput)}
              />
            </div>
            {subtitleError && (
              <div className={cx(css.error, errorStyle)}>
                {t(CUSTOMIZER.FIELD_TOO_LONG)}
              </div>
            )}
          </>
        )
      case CUSTOMIZATION_TAGS.LABEL_WITH_IMAGE:
        return (
          <>
            <div className={cx(css.variantsImages)}>
              {variants?.length > 1 &&
                variants?.map((item) => {
                  return (
                    <VariantImageSelector
                      key={item?.id}
                      ariaLabel={`${t(
                        PRODUCTS_TYPES.TYPE(product.productType),
                      )}: ${item?.selectedOptions?.[0]?.value}`}
                      className={cx(css.variantImageSelector)}
                      active={item?.id === activeVariant?.id}
                      onClick={() => setActiveVariant(item)}
                      item={item}
                    />
                  )
                })}
            </div>
            <div className={css.fileInputContainer}>
              <span className={cx(css.title, titleStyle)}>
                {t('customizer.file_input_label')}
              </span>
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                ref={inputRef}
                className={cx(css.fileInput, titleStyle)}
                onChange={async (event) => {
                  let nexcustomizationImageState = customizationImage
                  if (!event?.target?.files?.[0]) {
                    inputRef.current.value = ''
                    nexcustomizationImageState[activeTabIndex] = null
                    setCustomizationImage([...nexcustomizationImageState])
                    return
                  }
                  if (event?.target?.files?.[0].size > MAX_BYTES_SIZE) {
                    setFileError(true)
                    inputRef.current.value = ''
                    return
                  }
                  setFileError(false)
                  const base64 = await convertBlobToBase64(
                    event?.target?.files?.[0],
                  )
                  nexcustomizationImageState[activeTabIndex] = base64
                  setCustomizationImage([...nexcustomizationImageState])
                }}
              />
              {fileError && (
                <div className={cx(css.error, errorStyle)}>
                  {t(CUSTOMIZER.FILE_TOO_LARGE)}
                </div>
              )}
            </div>
            <BlurredCta
              theme={GlobalThemeColors.Shade1}
              className={css.deleteFileCta}
              onClick={() => {
                const nextState = customizationImage
                nextState[activeTabIndex] = null
                setCustomizationImage([...nextState])
              }}>
              {t(CTA.DELETE)}
            </BlurredCta>
            {TitleInput()}
            <div
              className={cx(css.textareaContainer, {
                hide: !activeVariant?.isLabelWithSubtitle,
              })}>
              <Input
                textarea
                onKeyDown={() => setLastActive('subtitle')}
                value={subtitle}
                onChange={(e) => {
                  setTyping(true)
                  setSubtitle(e.currentTarget.value)
                  onType()
                }}
                placeholder={t(CUSTOMIZER.LABEL_SUBTITLE)}
                className={cx(textareaTextStyle, css.subtitleInput)}
              />
            </div>
            {subtitleError && (
              <div className={cx(css.error, errorStyle)}>
                {t(CUSTOMIZER.FIELD_TOO_LONG)}
              </div>
            )}
          </>
        )
    }
  }
  const activeImage = customizationImage[activeTabIndex]

  if (disabled) return null
  return (
    <div className={cx(css.LabelCustomization, className)}>
      <div
        className={cx(css.titleRow)}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label={product?.customizationType}>
        <Checkbox
          checked={active}
          onChange={() => {
            null
          }}
          className={titleStyle}>
          {tags.includes(CUSTOMIZATION_TAGS.LEGEND)
            ? t(PRODUCT.CUSTOMIZATION(CUSTOMIZATION_TAGS.LEGEND))
            : t(PRODUCT.CUSTOMIZATION(product.customizationType))}
        </Checkbox>
        {priceDifference && <Tag>{priceDifference}</Tag>}
      </div>
      {active && (
        <>
          <Ratio
            className={cx(css.preview, {
              [css.hideOnMobile]: shouldHidePreviewOnMobile,
            })}
            ratio={1}>
            {() => (
              <div
                className={cx(css.previewContainer)}
                dangerouslySetInnerHTML={{
                  __html: currentLabelInputsContent?.html
                    ?.replace(IMAGE_PLACEHOLDER, activeImage ?? '#')
                    .replace('font-size: 17px;', ''),
                }}
              />
            )}
          </Ratio>

          <div className={cx(css.customContainer)}>
            {customization(product?.customizationType)}
          </div>
        </>
      )}
    </div>
  )
}

function isLabelWithSubtitle(
  product: SerializedProduct,
  activeVariant: Variant,
) {
  return (
    (product?.customizationType === CUSTOMIZATION_TAGS.ENGRAVING &&
      activeVariant?.isEngravingWithSubtitle) ||
    (product?.customizationType === CUSTOMIZATION_TAGS.LABEL &&
      activeVariant?.isLabelWithSubtitle) ||
    (product.customizationType === CUSTOMIZATION_TAGS.LABEL_WITH_IMAGE &&
      activeVariant?.isLabelWithSubtitle)
  )
}
